import * as XLSX from "xlsx";

// Sessions are billed in South African Rand - stamp an explicit "R" number
// format on the rate/amount columns so the workbook never reads as
// unlabeled (or worse, dollar-formatted) numbers once opened in Excel.
const RAND_FORMAT = 'R#,##0.00;(R#,##0.00)';

const applyCurrencyFormat = (sheet, colIndexes, rowCount) => {
  // Row 0 is the header, so data starts at row 1.
  for (let r = 1; r <= rowCount; r++) {
    for (const c of colIndexes) {
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = sheet[addr];
      if (cell && typeof cell.v === "number") {
        cell.z = RAND_FORMAT;
      }
    }
  }
};

// Generates the tutor-by-tutor, session-by-session audit workbook for a
// payroll run, from the exact session rows processAndDownloadPayroll already
// fetched via get_payroll_sessions - the same rows used to build the Sage
// upload file, from BEFORE finalize_payroll marked them paid. This
// deliberately does NOT re-query get_payroll_sessions itself: that RPC
// filters on is_paid = false, and by the time this runs, finalize_payroll
// has already flipped those exact sessions to paid - a fresh query here
// would always come back empty.
export const downloadTutorPayrollBreakdown = (
  sessions,
  payrollStart,
  payrollEnd,
) => {
  const lines = (sessions || []).map((s) => ({
    tutor: s.tutor_name,
    student: s.student_name,
    date: s.session_date,
    hours: Number(s.hours),
    rate: Number(s.rate),
    amount: Number(s.amount),
  }));

  if (lines.length === 0) {
    throw new Error(
      "No approved, unpaid sessions found in this date range - nothing to break down.",
    );
  }

  lines.sort((a, b) => {
    if (a.tutor !== b.tutor) return a.tutor.localeCompare(b.tutor);
    return a.date.localeCompare(b.date);
  });

  const totalHours = lines.reduce((acc, l) => acc + l.hours, 0);
  const totalAmount = lines.reduce((acc, l) => acc + l.amount, 0);

  // --- Session Detail sheet: every line that feeds the payroll total ---
  const detailHeader = ["Tutor", "Student", "Session Date", "Hours", "Rate", "Amount"];
  const detailRows = lines.map((l) => ({
    Tutor: l.tutor,
    Student: l.student,
    "Session Date": l.date,
    Hours: l.hours,
    Rate: l.rate,
    Amount: l.amount,
  }));
  detailRows.push({
    Tutor: "",
    Student: "",
    "Session Date": "TOTAL",
    Hours: Math.round(totalHours * 100) / 100,
    Rate: "",
    Amount: Math.round(totalAmount * 100) / 100,
  });

  const detailSheet = XLSX.utils.json_to_sheet(detailRows, {
    header: detailHeader,
  });
  detailSheet["!cols"] = [
    { wch: 24 },
    { wch: 26 },
    { wch: 14 },
    { wch: 10 },
    { wch: 10 },
    { wch: 12 },
  ];
  // Rate = column E (index 4), Amount = column F (index 5).
  applyCurrencyFormat(detailSheet, [4, 5], detailRows.length);

  // --- Tutor Summary sheet: sessions / hours / amount per tutor ---
  const summaryMap = new Map();
  lines.forEach((l) => {
    if (!summaryMap.has(l.tutor)) {
      summaryMap.set(l.tutor, { sessions: 0, hours: 0, amount: 0 });
    }
    const s = summaryMap.get(l.tutor);
    s.sessions += 1;
    s.hours += l.hours;
    s.amount += l.amount;
  });

  const summaryHeader = ["Tutor", "Sessions", "Total Hours", "Total Amount"];
  const summaryRows = Array.from(summaryMap.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([tutorName, s]) => ({
      Tutor: tutorName,
      Sessions: s.sessions,
      "Total Hours": Math.round(s.hours * 100) / 100,
      "Total Amount": Math.round(s.amount * 100) / 100,
    }));
  summaryRows.push({
    Tutor: "GRAND TOTAL",
    Sessions: lines.length,
    "Total Hours": Math.round(totalHours * 100) / 100,
    "Total Amount": Math.round(totalAmount * 100) / 100,
  });

  const summarySheet = XLSX.utils.json_to_sheet(summaryRows, {
    header: summaryHeader,
  });
  summarySheet["!cols"] = [
    { wch: 26 },
    { wch: 12 },
    { wch: 14 },
    { wch: 16 },
  ];
  // Total Amount = column D (index 3).
  applyCurrencyFormat(summarySheet, [3], summaryRows.length);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, summarySheet, "Tutor Summary");
  XLSX.utils.book_append_sheet(workbook, detailSheet, "Session Detail");

  XLSX.writeFile(
    workbook,
    `Tutor_Payroll_Breakdown_${payrollStart}_${payrollEnd}.xlsx`,
  );

  return { sessionCount: lines.length, totalHours, totalAmount };
};
